import React, { useState } from 'react';
import { Menu } from '@ofself/shell';
import { ChevronDown } from 'lucide-react';
import { FindBar } from './FacetRail';
import './Picker.css';

// THE PICKER — one of many, in a field that opens the house menu: a realm, a
// tool, a field, a kind. Never the browser's <select>: Chrome hands a native
// select's open state to the operating system, which paints a sheet no rule
// here can reach. It is the kit's Menu, each option a pick wearing its check;
// the field stands as tall as FindBar compact, so it lines up in a form. Five
// bare words or fewer are a Segmented.
//
//   <Picker label="Realm" value={v} onChange={setV} placeholder="Choose a realm"
//       options={[{ value: 'r1', label: 'Health' }, …]} />
//
// An option is { value, label, icon?, hint?, disabled?, group? }, or a plain
// string. The value goes back in the type it came in. Options with a `group`
// stand under its name. Past SEARCH_AT options (or `searchable`) a find field
// tops the menu: type to narrow, Enter takes the first, the down arrow walks
// the rows. `match(option, needle)` replaces the plain match on the words.
// `size` is 'sm' (a rail) or 'md' (the default); `fill` takes the width.

const SEARCH_AT = 12;

const asOption = (o) => (typeof o === 'string' ? { value: o, label: o } : o);
const same = (a, b) => String(a ?? '') === String(b ?? '');
// the words a find matches: the label (the value only when the label is not words; an id is not)
const wordsOf = (o) => `${typeof o.label === 'string' ? o.label : (o.value ?? '')} ${typeof o.hint === 'string' ? o.hint : ''}`.toLowerCase();

function Face({ o }) {
    return (
        <span className="nx-picker__face">
            {o.icon && <span className="nx-picker__glyph" aria-hidden="true">{o.icon}</span>}
            <span className="nx-picker__word">{o.label}</span>
        </span>
    );
}

export default function Picker({
    options = [], value, onChange, label, placeholder = 'Choose', disabled = false, title, size = 'md',
    fill = false, searchable, match, className = '', id, align = 'start',
}) {
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState('');
    const list = options.filter(Boolean).map(asOption);
    const chosen = list.find((o) => same(o.value, value));
    const find = searchable ?? list.length > SEARCH_AT;

    const needle = q.trim().toLowerCase();
    const shown = needle ? list.filter((o) => (match ? match(o, needle) : wordsOf(o).includes(needle))) : list;

    const toggle = (next) => { setOpen(next); if (!next) setQ(''); };
    const take = (o) => { if (!same(o.value, value)) onChange?.(o.value); };

    // grouped options stand under their group's name, the groups in the order they first come
    const groups = [];
    shown.forEach((o) => {
        const g = o.group || '';
        const at = groups.find((x) => x.name === g);
        if (at) at.rows.push(o); else groups.push({ name: g, rows: [o] });
    });
    const items = groups.flatMap(({ name, rows }) => [
        ...(name ? [{ header: name }] : []),
        ...rows.map((o) => ({
            label: <Face o={o} />,
            hint: o.hint,
            disabled: o.disabled,
            selected: same(o.value, value),
            onSelect: () => take(o),
        })),
    ]);
    if (!items.length) items.push({ header: 'No matches' });

    // KEYS IN THE FIND FIELD stay there: the menu's own type-to-jump would take
    // the focus off the field at the first letter.
    const onFindKey = (e) => {
        if (e.key === 'Escape') return;
        e.stopPropagation();
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            e.currentTarget.closest('[role="menu"]')?.querySelector('[role="menuitem"]:not([data-disabled])')?.focus();
        } else if (e.key === 'Enter') {
            e.preventDefault();
            const first = shown.find((o) => !o.disabled);
            if (first) { take(first); toggle(false); }
        }
    };

    const trigger = (
        <button
            type="button"
            id={id}
            className={`nx-picker is-${size}${fill ? ' is-fill' : ''}${className ? ` ${className}` : ''}`}
            aria-label={label}
            title={title}
            disabled={disabled}
            data-value={value == null ? '' : String(value)}
        >
            {chosen ? <Face o={chosen} /> : <span className="nx-picker__face is-empty">{placeholder}</span>}
            <ChevronDown className="nx-picker__chev" size={size === 'sm' ? 12 : 14} strokeWidth={1.8} aria-hidden="true" />
        </button>
    );

    // held still, there is nothing to open
    if (disabled) return trigger;
    return (
        <Menu
            trigger={trigger}
            open={open}
            onOpenChange={toggle}
            align={align}
            sideOffset={4}
            contentClassName={`nx-picker__menu is-${size}`}
            header={find ? (
                <div className="nx-picker__find" onKeyDown={onFindKey}>
                    <FindBar compact autoFocus value={q} onChange={setQ} placeholder="Find" label={label ? `Find in ${label}` : 'Find'} />
                </div>
            ) : null}
            items={items}
        />
    );
}

export { Picker };
